import { setLocalAuthKey } from '@/common/auth';
import { CompactButton } from '@/components/buttons';
import { StandardButton } from '@/components/buttons/standard';
import { ContentPanel } from '@/components/content-panel';
import { CustomContentSection } from '@/components/custom-content-section';
import { useLayoutContext } from '@/contexts';
import { PrimaryLayout } from '@/layouts/primary';
import { Flex, Title } from '@mantine/core';
import { IconX } from '@tabler/icons-react';
import { useNavigate } from '@tanstack/react-router';
import { FormikProps } from 'formik';
import { useCallback, useMemo, useRef } from 'react';
import {
	ContentSectionLocation,
	ContentSectionPlacement,
} from '../../../../backend/src/types.generated';
import { ChangePasswordForm, ChangePasswordFormValues } from './form';
import { useChangePasswordMutation } from './graphql.generated';

export const ChangePasswordPage = () => {
	const navigate = useNavigate();
	const [changePassword, { error }] = useChangePasswordMutation();
	const ref = useRef<FormikProps<ChangePasswordFormValues>>(null);
	const { isDesktop } = useLayoutContext();
	const changePasswordButton = useMemo(
		() => (
			<StandardButton onClick={() => ref.current?.submitForm()}>Change Password</StandardButton>
		),
		[]
	);

	const homeButton = useMemo(
		() => <StandardButton onClick={() => navigate({ to: '/home' })}>Home</StandardButton>,
		[navigate]
	);

	const closeButton = useMemo(
		() => (
			<CompactButton miw={80} leftSection={<IconX />} onClick={() => navigate({ to: '/account' })}>
				Close
			</CompactButton>
		),
		[navigate]
	);

	const handleSubmit = useCallback(
		async (values: ChangePasswordFormValues) => {
			if (values.password !== values.reenterPassword) {
				ref.current?.setFieldError('reenterPassword', 'Passwords do not match');
				return;
			}

			try {
				const { data } = await changePassword({
					variables: {
						newPassword: values.password,
					},
				});

				if (data?.changePassword?.authToken) {
					await setLocalAuthKey(data.changePassword.authToken);
					navigate({ to: '/account' });
				}
			} catch (error) {
				console.error(error);
			}
		},
		[changePassword, navigate]
	);

	return (
		<PrimaryLayout
			header={{ title: 'Change Password', backTo: '/account', children: closeButton }}
			footer={{ children: [error ? homeButton : changePasswordButton] }}
		>
			<ContentPanel width="sm">
				<Flex direction="column" h="100%" w="100%" flex={1} justify="center">
					<CustomContentSection
						location={ContentSectionLocation.ChangePassword}
						placement={ContentSectionPlacement.Top}
					/>
					{error ? (
						<Title order={2} ta="center" mt="xl">
							An error occurred, please try again later or contact{' '}
							<a
								href="mailto:support@curbcycle.com.au?subject=Technical issue on Change Password page of Curby App"
								target="_blank"
								rel="noopener"
							>
								support@curbcycle.com.au
							</a>
						</Title>
					) : (
						<ChangePasswordForm onSubmit={handleSubmit} formRef={ref} />
					)}
					<CustomContentSection
						location={ContentSectionLocation.ChangePassword}
						placement={ContentSectionPlacement.Middle}
					/>
					<CustomContentSection
						location={ContentSectionLocation.ChangePassword}
						placement={ContentSectionPlacement.Bottom}
					/>
				</Flex>
			</ContentPanel>
			{isDesktop && (
				<Flex
					direction="row"
					align="center"
					justify="center"
					pos="fixed"
					w="100%"
					bg="white"
					bottom={0}
					py="sm"
					bd="1px solid #e0e0e0"
				>
					<Flex w={300}>{error ? homeButton : changePasswordButton}</Flex>
				</Flex>
			)}
		</PrimaryLayout>
	);
};
